import React, { useEffect, useState, useMemo } from "react";
import { Formik, Form } from "formik";
import { object, string } from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStore,
  faPlus,
  faSearch,
  faPen,
  faTimes,
  faSave,
  faCheckCircle,
  faTimesCircle,
  faSyncAlt,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faUser,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import getAllShops from "../../components/Admin/getAllShops";
import addNewShop from "../../components/Admin/addNewShop";
import editShopInfo from "../../components/Admin/editShopInfo";
import getShopDetails from "../../components/Admin/getShopDetails";
import deleteDealer from "../../components/Admin/deleteDealer";
import ReactSelect from "react-select";
import stateDistrictTalukaData from "../../assets/static/IndiaStateDistrictTaluka";

const C = {
  dark: "#1a3328",
  mid: "#2d5a3d",
  bright: "#3d8c5e",
  light: "#e8f5ee",
  page: "#f4f6f5",
  border: "#d8e8dc",
  white: "#ffffff",
  textDk: "#1a2e22",
  textGr: "#6c757d",
  red: "#c62828",
  redBg: "#fdecea",
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    fontSize: "0.87rem",
    border: `1.5px solid ${state.isFocused ? C.bright : C.border}`,
    boxShadow: state.isFocused ? `0 0 0 2px rgba(61,140,94,0.15)` : "none",
    "&:hover": { borderColor: C.bright },
    backgroundColor: C.white,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.85rem",
    backgroundColor: state.isSelected ? C.mid : state.isFocused ? C.light : "#fff",
    color: state.isSelected ? "#fff" : C.textDk,
  }),
  placeholder: (base) => ({ ...base, color: "#aab8b0", fontSize: "0.85rem" }),
  singleValue: (base) => ({ ...base, color: C.textDk, fontSize: "0.87rem" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  valueContainer: (base) => ({ ...base, padding: "0 0.6rem" }),
};

const DEALER_COLORS = [
  "#2d5a3d",
  "#3b82f6",
  "#d4a853",
  "#7b1fa2",
  "#c62828",
  "#2d7a45",
  "#1e6fc4",
];
const DEALER_TYPES = [
  "Retailer",
  "Wholesaler",
  "Distributor",
  "Sub-Dealer",
  "Other",
];

const addSchema = object().shape({
  shopName: string().nullable().required("Required"),
  ownerName: string()
    .nullable()
    .required("Required")
    .matches(/^[a-zA-Z ]*$/, "Text only"),
  mobileNumber: string()
    .nullable()
    .required("Required")
    .matches(/^\d{10}$/, "10 digits required"),
  emailID: string().nullable().email("Invalid email"),
  pinCode: string()
    .nullable()
    .matches(/^\d{6}$/, "Must be 6 digits"),
});

const editSchema = object().shape({
  shopName: string().nullable().required("Required"),
  ownerName: string()
    .nullable()
    .required("Required")
    .matches(/^[a-zA-Z ]*$/, "Text only"),
  mobileNumber: string()
    .nullable()
    .required("Required")
    .matches(/^\d{10}$/, "10 digits required"),
  emailID: string().nullable().email("Invalid email"),
  pinCode: string()
    .nullable()
    .matches(/^\d{6}$/, "Must be 6 digits"),
});

const dealerInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

/* ─── Label ────────────────────────────────────────────────────────────────── */
const Lbl = ({ children }) => (
  <label
    style={{
      fontSize: "0.75rem",
      fontWeight: 600,
      color: C.textGr,
      display: "block",
      marginBottom: "0.3rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    }}
  >
    {children}
  </label>
);

/* ─── Input field ──────────────────────────────────────────────────────────── */
const Field = ({
  label,
  name,
  type = "text",
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  disabled,
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <Lbl>{label}</Lbl>
    <input
      type={type}
      name={name}
      value={values[name] || ""}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      style={{
        width: "100%",
        height: 40,
        borderRadius: 8,
        boxSizing: "border-box",
        border: `1.5px solid ${touched[name] && errors[name] ? C.red : C.border}`,
        padding: "0 0.8rem",
        fontSize: "0.87rem",
        outline: "none",
        color: C.textDk,
        backgroundColor: disabled ? "#f5f5f5" : C.white,
      }}
    />
    {touched[name] && errors[name] && (
      <div style={{ fontSize: "0.72rem", color: C.red, marginTop: "0.25rem" }}>
        {errors[name]}
      </div>
    )}
  </div>
);

/* ─── Select field ─────────────────────────────────────────────────────────── */
const SelectField = ({
  label,
  name,
  options,
  placeholder,
  values,
  handleChange,
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <Lbl>{label}</Lbl>
    <select
      name={name}
      value={values[name] || ""}
      onChange={handleChange}
      style={{
        width: "100%",
        height: 40,
        borderRadius: 8,
        boxSizing: "border-box",
        border: `1.5px solid ${C.border}`,
        padding: "0 0.8rem",
        fontSize: "0.87rem",
        outline: "none",
        color: C.textDk,
        backgroundColor: C.white,
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

/* ─── Section divider ──────────────────────────────────────────────────────── */
const Section = ({ title }) => (
  <div
    style={{
      marginBottom: "0.75rem",
      marginTop: "0.5rem",
      paddingBottom: "0.4rem",
      borderBottom: `1.5px solid ${C.border}`,
    }}
  >
    <span
      style={{
        fontSize: "0.7rem",
        fontWeight: 700,
        color: C.mid,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
      }}
    >
      {title}
    </span>
  </div>
);

/* ─── Drawer ───────────────────────────────────────────────────────────────── */
function DealerDrawer({ open, mode, shopData, onClose, onSuccess }) {
  const [apiError, setApiError] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTaluka, setSelectedTaluka] = useState(null);
  const isAdd = mode === "add";

  const emptyVals = {
    shopName: "",
    ownerName: "",
    mobileNumber: "",
    emailID: "",
    village: "",
    taluka: "",
    district: "",
    state: "",
    pinCode: "",
    dealerType: "",
    assignedSalesOfficer: "",
    status: "active",
  };

  useEffect(() => {
    if (open && !isAdd && shopData) {
      setSelectedState(shopData.state ? { value: shopData.state, label: shopData.state } : null);
      setSelectedDistrict(shopData.district ? { value: shopData.district, label: shopData.district } : null);
      const talukaVal = shopData.taluka || shopData.tehsil || "";
      setSelectedTaluka(talukaVal ? { value: talukaVal, label: talukaVal } : null);
    } else if (open && isAdd) {
      setSelectedState(null);
      setSelectedDistrict(null);
      setSelectedTaluka(null);
    }
  }, [open, isAdd, shopData]);

  const stateOptions = Object.keys(stateDistrictTalukaData).map((s) => ({ value: s, label: s }));
  const districtOptions = selectedState
    ? Object.keys(stateDistrictTalukaData[selectedState.value] || {}).map((d) => ({ value: d, label: d }))
    : [];
  const talukaOptions = selectedState && selectedDistrict
    ? (stateDistrictTalukaData[selectedState.value]?.[selectedDistrict.value] || []).map((t) => ({ value: t, label: t }))
    : [];

  const handleSubmit = async (values) => {
    setSaving(true);
    setApiError("");
    const payload = {
      ...values,
      state: selectedState?.value || "",
      district: selectedDistrict?.value || "",
      taluka: selectedTaluka?.value || "",
    };
    const res = isAdd ? await addNewShop(payload) : await editShopInfo(payload);
    setSaving(false);
    if (res?.status === 200 || res?.status === 201) {
      onSuccess();
      onClose();
    } else {
      const msg =
        res?.data?.message ||
        (isAdd
          ? "Error adding dealer. Check if mobile number already exists."
          : "Error updating dealer.");
      setApiError(msg);
    }
  };

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1040,
            backgroundColor: "rgba(0,0,0,0.35)",
          }}
        />
      )}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          zIndex: 1050,
          backgroundColor: C.white,
          boxShadow: "-4px 0 28px rgba(0,0,0,0.18)",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: C.dark,
            padding: "1rem 1.25rem",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: C.mid,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faStore}
                style={{ color: "#fff", fontSize: "0.8rem" }}
              />
            </div>
            <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>
              {isAdd ? "Add Dealer" : "Edit Dealer"}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.12)",
              border: "none",
              borderRadius: 7,
              width: 30,
              height: 30,
              cursor: "pointer",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: "0.85rem" }} />
          </button>
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          <Formik
            key={open ? (isAdd ? "add" : shopData?._id) : "closed"}
            enableReinitialize
            initialValues={isAdd ? emptyVals : { ...emptyVals, ...shopData }}
            validationSchema={isAdd ? addSchema : editSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit: submit,
            }) => (
              <Form style={{ padding: "1.25rem" }}>
                <Section title="Dealer Information" />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 0.85rem",
                  }}
                >
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Field
                      label="Dealer's Shop Name *"
                      name="shopName"
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Field
                      label="Owner Name *"
                      name="ownerName"
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </div>
                  <div>
                    <SelectField
                      label="Dealer Type"
                      name="dealerType"
                      options={DEALER_TYPES}
                      placeholder="Select type"
                      values={values}
                      handleChange={handleChange}
                    />
                  </div>
                  <div>
                    <Field
                      label="Assigned Sales Officer"
                      name="assignedSalesOfficer"
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </div>
                </div>

                <Section title="Contact Details" />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 0.85rem",
                  }}
                >
                  <div>
                    <Field
                      label="Mobile Number *"
                      name="mobileNumber"
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      disabled={!isAdd}
                    />
                  </div>
                  <div>
                    <Field
                      label="Email ID"
                      name="emailID"
                      type="email"
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </div>
                </div>

                <Section title="Location" />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 0.85rem",
                  }}
                >
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Field
                      label="Village / Town / City"
                      name="village"
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </div>
                  <div style={{ marginBottom: "1rem" }}>
                    <Lbl>State</Lbl>
                    <ReactSelect
                      placeholder="Select State"
                      value={selectedState}
                      onChange={(opt) => { setSelectedState(opt); setSelectedDistrict(null); setSelectedTaluka(null); }}
                      options={stateOptions}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isClearable
                    />
                  </div>
                  <div style={{ marginBottom: "1rem" }}>
                    <Lbl>District</Lbl>
                    <ReactSelect
                      placeholder={selectedState ? "Select District" : "Select State first"}
                      value={selectedDistrict}
                      onChange={(opt) => { setSelectedDistrict(opt); setSelectedTaluka(null); }}
                      isDisabled={!selectedState}
                      options={districtOptions}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isClearable
                    />
                  </div>
                  <div style={{ marginBottom: "1rem" }}>
                    <Lbl>Taluka</Lbl>
                    <ReactSelect
                      placeholder={selectedDistrict ? "Select Taluka" : "Select District first"}
                      value={selectedTaluka}
                      onChange={setSelectedTaluka}
                      isDisabled={!selectedDistrict}
                      options={talukaOptions}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isClearable
                    />
                  </div>
                  <div>
                    <Field
                      label="Pincode"
                      name="pinCode"
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </div>
                </div>

                {!isAdd && (
                  <>
                    <Section title="Status" />
                    <div style={{ marginBottom: "1rem" }}>
                      <Lbl>Dealer Status</Lbl>
                      <select
                        name="status"
                        value={values.status || "active"}
                        onChange={handleChange}
                        style={{
                          width: "100%",
                          height: 40,
                          borderRadius: 8,
                          border: `1.5px solid ${C.border}`,
                          padding: "0 0.8rem",
                          fontSize: "0.87rem",
                          outline: "none",
                          color: C.textDk,
                          backgroundColor: C.white,
                          boxSizing: "border-box",
                        }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </>
                )}

                {apiError && (
                  <div
                    style={{
                      padding: "0.6rem 0.85rem",
                      borderRadius: 8,
                      marginBottom: "1rem",
                      backgroundColor: C.redBg,
                      color: C.red,
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {apiError}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: "0.5rem" }}>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={saving}
                    style={{
                      flex: 1,
                      height: 42,
                      borderRadius: 9,
                      border: "none",
                      backgroundColor: C.mid,
                      color: "#fff",
                      fontSize: "0.87rem",
                      fontWeight: 600,
                      cursor: saving ? "not-allowed" : "pointer",
                      opacity: saving ? 0.7 : 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSave}
                      style={{ fontSize: "0.75rem" }}
                    />
                    {saving ? "Saving…" : isAdd ? "Add Dealer" : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      height: 42,
                      padding: "0 18px",
                      borderRadius: 9,
                      border: `1.5px solid ${C.border}`,
                      backgroundColor: C.white,
                      color: C.textGr,
                      fontSize: "0.87rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
}

/* ─── Delete confirm ───────────────────────────────────────────────────────── */
function DeleteConfirm({ name, onConfirm, onCancel }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1060,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          backgroundColor: C.white,
          borderRadius: 12,
          padding: "1.75rem",
          width: 360,
          boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: C.redBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 1rem",
          }}
        >
          <FontAwesomeIcon
            icon={faTrash}
            style={{ color: C.red, fontSize: "1.1rem" }}
          />
        </div>
        <div
          style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1rem",
            color: C.textDk,
            marginBottom: 6,
          }}
        >
          Delete Dealer
        </div>
        <div
          style={{
            textAlign: "center",
            fontSize: "0.85rem",
            color: C.textGr,
            marginBottom: "1.5rem",
          }}
        >
          Are you sure you want to delete <strong>{name}</strong>? This action
          cannot be undone.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onConfirm}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 9,
              border: "none",
              backgroundColor: C.red,
              color: "#fff",
              fontSize: "0.87rem",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Yes, Delete
          </button>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 9,
              border: `1.5px solid ${C.border}`,
              backgroundColor: C.white,
              color: C.textGr,
              fontSize: "0.87rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Dealer row ───────────────────────────────────────────────────────────── */
function DealerRow({ dealer, index, onEdit, onDelete }) {
  const [hover, setHover] = useState(false);
  const active = dealer.status !== "inactive";
  const location = [dealer.village, dealer.district, dealer.state]
    .filter(Boolean)
    .join(", ");

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: "0.75rem 1rem",
        backgroundColor: hover
          ? C.light
          : index % 2 === 0
            ? C.white
            : "#f8fbf9",
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.12s",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          flexShrink: 0,
          backgroundColor: DEALER_COLORS[index % DEALER_COLORS.length],
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.82rem",
          fontWeight: 700,
        }}
      >
        {dealerInitials(dealer.shopName)}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{ fontWeight: 700, fontSize: "0.9rem", color: C.textDk }}
          >
            {dealer.shopName}
          </span>
          {dealer.dealerType && (
            <span
              style={{
                fontSize: "0.72rem",
                color: C.white,
                backgroundColor: C.mid,
                padding: "1px 8px",
                borderRadius: 10,
                fontWeight: 600,
              }}
            >
              {dealer.dealerType}
            </span>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginTop: 3,
            flexWrap: "wrap",
          }}
        >
          {dealer.ownerName && (
            <span
              style={{
                fontSize: "0.73rem",
                color: C.textGr,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <FontAwesomeIcon icon={faUser} style={{ fontSize: "0.6rem" }} />{" "}
              {dealer.ownerName}
            </span>
          )}
          <span
            style={{
              fontSize: "0.73rem",
              color: C.textGr,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <FontAwesomeIcon icon={faPhone} style={{ fontSize: "0.6rem" }} />{" "}
            {dealer.mobileNumber}
          </span>
          {location && (
            <span
              style={{
                fontSize: "0.73rem",
                color: C.textGr,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                style={{ fontSize: "0.6rem" }}
              />{" "}
              {location}
            </span>
          )}
          {dealer.emailID && (
            <span
              style={{
                fontSize: "0.73rem",
                color: C.textGr,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                style={{ fontSize: "0.6rem" }}
              />{" "}
              {dealer.emailID}
            </span>
          )}
          {dealer.assignedSalesOfficer && (
            <span style={{ fontSize: "0.73rem", color: C.textGr }}>
              SO: {dealer.assignedSalesOfficer}
            </span>
          )}
        </div>
      </div>

      <span
        style={{
          fontSize: "0.7rem",
          fontWeight: 700,
          padding: "3px 10px",
          borderRadius: 10,
          backgroundColor: active ? C.light : C.redBg,
          color: active ? C.bright : C.red,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {active ? "Active" : "Inactive"}
      </span>

      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button
          onClick={() => onEdit(dealer)}
          style={{
            height: 32,
            padding: "0 12px",
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            backgroundColor: hover ? C.white : "transparent",
            color: C.mid,
            fontSize: "0.75rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            transition: "background 0.12s",
          }}
        >
          <FontAwesomeIcon icon={faPen} style={{ fontSize: "0.6rem" }} /> Edit
        </button>
        <button
          onClick={() => onDelete(dealer)}
          style={{
            height: 32,
            padding: "0 12px",
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            backgroundColor: hover ? C.redBg : "transparent",
            color: C.red,
            fontSize: "0.75rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            transition: "background 0.12s",
          }}
        >
          <FontAwesomeIcon icon={faTrash} style={{ fontSize: "0.6rem" }} />
        </button>
      </div>
    </div>
  );
}

/* ─── Main page ────────────────────────────────────────────────────────────── */
const Shop = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [drawer, setDrawer] = useState({
    open: false,
    mode: "add",
    shop: null,
  });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await getAllShops();
    if (res?.status === 200 && Array.isArray(res.data)) setDealers(res.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      dealers.filter((d) => {
        const q = search.toLowerCase();
        const matchSearch =
          (d.shopName || "").toLowerCase().includes(q) ||
          (d.ownerName || "").toLowerCase().includes(q) ||
          (d.mobileNumber || "").includes(q) ||
          (d.district || "").toLowerCase().includes(q) ||
          (d.dealerType || "").toLowerCase().includes(q) ||
          (d.assignedSalesOfficer || "").toLowerCase().includes(q) ||
          (d.emailID || "").toLowerCase().includes(q);
        const matchStatus = filterStatus === "all" || d.status === filterStatus;
        return matchSearch && matchStatus;
      }),
    [dealers, search, filterStatus],
  );

  const openAdd = () => setDrawer({ open: true, mode: "add", shop: null });
  const openEdit = async (d) => {
    const res = await getShopDetails(d._id || d.id);
    const data =
      res?.status === 200 && Array.isArray(res.data) && res.data[0]
        ? res.data[0]
        : d;
    setDrawer({ open: true, mode: "edit", shop: data });
  };
  const closeDrawer = () => setDrawer((d) => ({ ...d, open: false }));

  const handleDelete = async (dealer) => {
    const res = await deleteDealer(dealer._id || dealer.id);
    if (res?.status === 200) {
      setConfirmDelete(null);
      load();
    } else setConfirmDelete(null);
  };

  const activeCount = dealers.filter((d) => d.status !== "inactive").length;
  const inactiveCount = dealers.length - activeCount;

  return (
    <div
      style={{
        backgroundColor: C.page,
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Page Header */}
      <div
        style={{
          backgroundColor: C.white,
          padding: "0.85rem 1.5rem",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${C.border}`,
          boxShadow: "0 1px 4px rgba(20,50,30,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              backgroundColor: C.light,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faStore}
              style={{ color: C.mid, fontSize: "0.9rem" }}
            />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1rem", color: C.textDk }}>
              Dealer Management
            </div>
            <div style={{ fontSize: "0.72rem", color: C.textGr }}>
              {dealers.length} dealers · {activeCount} active · {inactiveCount}{" "}
              inactive
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={load}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: 34,
              padding: "0 12px",
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              backgroundColor: C.white,
              color: C.textGr,
              fontSize: "0.8rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faSyncAlt} style={{ fontSize: "0.72rem" }} />{" "}
            Refresh
          </button>
          <button
            onClick={openAdd}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: 34,
              padding: "0 14px",
              borderRadius: 8,
              border: "none",
              backgroundColor: C.mid,
              color: "#fff",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: "0.72rem" }} />{" "}
            Add Dealer
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          padding: "1.25rem 1.5rem 0",
          flexShrink: 0,
        }}
      >
        {[
          {
            icon: faStore,
            label: "Total Dealers",
            value: dealers.length,
            bg: C.light,
            color: C.mid,
            filter: "all",
          },
          {
            icon: faCheckCircle,
            label: "Active",
            value: activeCount,
            bg: "#e8f5e9",
            color: "#2e7d32",
            filter: "active",
          },
          {
            icon: faTimesCircle,
            label: "Inactive",
            value: inactiveCount,
            bg: C.redBg,
            color: C.red,
            filter: "inactive",
          },
        ].map(({ icon, label, value, bg, color, filter }) => (
          <div
            key={label}
            onClick={() =>
              setFilterStatus((f) => (f === filter ? "all" : filter))
            }
            style={{
              backgroundColor: C.white,
              borderRadius: 10,
              padding: "0.8rem 1.1rem",
              border: `1.5px solid ${filterStatus === filter ? color : C.border}`,
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(20,50,30,0.05)",
              transition: "border-color 0.15s",
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FontAwesomeIcon
                icon={icon}
                style={{ color, fontSize: "0.88rem" }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 800,
                  color: C.textDk,
                  lineHeight: 1,
                }}
              >
                {value}
              </div>
              <div
                style={{ fontSize: "0.7rem", color: C.textGr, marginTop: 2 }}
              >
                {label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* List */}
      <div style={{ flex: 1, padding: "1.25rem 1.5rem", overflowY: "auto" }}>
        <div
          style={{
            backgroundColor: C.white,
            borderRadius: 12,
            border: `1px solid ${C.border}`,
            boxShadow: "0 2px 12px rgba(20,50,30,0.05)",
            overflow: "hidden",
          }}
        >
          {/* Toolbar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.85rem 1rem",
              borderBottom: `1px solid ${C.border}`,
              backgroundColor: "#f8faf9",
            }}
          >
            <div style={{ flex: 1, position: "relative" }}>
              <FontAwesomeIcon
                icon={faSearch}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: C.textGr,
                  fontSize: "0.75rem",
                }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by shop name, owner, mobile, dealer type, district…"
                style={{
                  width: "100%",
                  height: 36,
                  borderRadius: 8,
                  border: `1.5px solid ${C.border}`,
                  paddingLeft: 30,
                  paddingRight: 10,
                  fontSize: "0.83rem",
                  outline: "none",
                  color: C.textDk,
                  backgroundColor: C.white,
                  boxSizing: "border-box",
                }}
              />
            </div>
            <span
              style={{
                fontSize: "0.78rem",
                color: C.textGr,
                whiteSpace: "nowrap",
              }}
            >
              {filtered.length} dealer{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "4rem",
                color: C.textGr,
                fontSize: "0.9rem",
              }}
            >
              Loading dealers…
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "4rem", color: C.textGr }}
            >
              <FontAwesomeIcon
                icon={faStore}
                style={{
                  fontSize: "2.5rem",
                  color: C.border,
                  display: "block",
                  marginBottom: "0.75rem",
                }}
              />
              <div style={{ fontWeight: 600 }}>
                {search
                  ? "No dealers match your search"
                  : "No dealers added yet"}
              </div>
              {!search && (
                <div style={{ fontSize: "0.82rem", marginTop: 5 }}>
                  Click "Add Dealer" to get started
                </div>
              )}
            </div>
          ) : (
            filtered.map((d, i) => (
              <DealerRow
                key={d._id || d.id || d.mobileNumber}
                dealer={d}
                index={i}
                onEdit={openEdit}
                onDelete={(dealer) => setConfirmDelete(dealer)}
              />
            ))
          )}
        </div>
      </div>

      <DealerDrawer
        open={drawer.open}
        mode={drawer.mode}
        shopData={drawer.shop}
        onClose={closeDrawer}
        onSuccess={() => {
          closeDrawer();
          load();
        }}
      />

      {confirmDelete && (
        <DeleteConfirm
          name={confirmDelete.shopName}
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default Shop;
